import React, { useEffect, useRef, useState } from "react";
import Arrow from "../../elements/Arrow/Arrow";
import { Cta } from "../../elements/Cta/Cta";
import { LinkBarProps } from "./LinkBar";
import * as styles from "./MobileLinkCarousel.module.css";

interface Props extends LinkBarProps {}

export const MobileLinkCarousel: React.FC<Props> = ({ links }) => {
  if (typeof window === "undefined") return null;
  const activeIdx = links.findIndex((link) => link.active);

  const [centerLinkIdx, setCenterLinkIdx] = useState(
    activeIdx >= 0 ? activeIdx : 0
  );

  const calculateLeft = (idx: number) =>
    idx * -175 + (window.innerWidth - 175) / 2;

  const [leftPosition, setLeftPosition] = useState(
    calculateLeft(centerLinkIdx)
  );

  const barRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const newLeft = calculateLeft(centerLinkIdx);
    setLeftPosition(newLeft);
  }, [centerLinkIdx, window?.innerWidth]);

  useEffect(() => {
    if (barRef && barRef?.current) {
      let downX = 0;

      const unifyMouseTouch = (event: MouseEvent | TouchEvent) =>
        //@ts-ignore
        event.changedTouches ? event.changedTouches[0] : event;
      const handleTouchStart = (event: MouseEvent | TouchEvent) => {
        const e = unifyMouseTouch(event);
        downX = e.clientX;
      };

      const handleTouchEnd = (event: MouseEvent | TouchEvent) => {
        const e = unifyMouseTouch(event);

        if (e.clientX > downX && centerLinkIdx < links.length - 1) {
          const newIdx = centerLinkIdx + 1;
          setCenterLinkIdx(newIdx);
        }

        if (e.clientX < downX && centerLinkIdx > 0) {
          const newIdx = centerLinkIdx - 1;
          setCenterLinkIdx(newIdx);
        }
      };

      barRef.current.addEventListener("mousedown", handleTouchStart, false);
      barRef.current.addEventListener("touchstart", handleTouchStart, false);

      barRef.current.addEventListener("mouseup", handleTouchEnd, false);
      barRef.current.addEventListener("touchend", handleTouchEnd, false);

      return () => {
        barRef.current?.removeEventListener("touchend", handleTouchEnd);
        barRef.current?.removeEventListener("mouseup", handleTouchEnd);
        barRef.current?.removeEventListener("mousedown", handleTouchStart);
        barRef.current?.removeEventListener("touchend", handleTouchStart);
      };
    }
  }, []);

  useEffect(() => {
    const recalculateLeft = () => {
      if (window.innerWidth > 1240) return;
      setLeftPosition(calculateLeft(centerLinkIdx));
    };

    window.addEventListener("resize", recalculateLeft);

    return () => {
      window.removeEventListener("resize", recalculateLeft);
    };
  }, [window]);

  return (
    <nav className={styles.linkcarousel}>
      <div
        className={styles.links}
        style={{ left: leftPosition }}
        ref={barRef}
        data-kontent-element-codename="link_configuration__links"
      >
        {links?.map((link, idx) => (
          <a
            href={link.href}
            target={link.target}
            className={link.active ? styles.active : undefined}
            key={idx + link.href}
          >
            {link.label || link.pageTitle}
          </a>
        ))}
      </div>
      <div className={styles.controls}>
        <Cta
          button
          label={
            <Arrow
              direction="left"
              className={styles.arrow}
              color={centerLinkIdx > 0 ? "#666666" : "#ededed"}
            />
          }
          buttonStyle="white"
          disabled={!(centerLinkIdx > 0)}
          onClick={() => setCenterLinkIdx(centerLinkIdx - 1)}
        ></Cta>
        <Cta
          button
          buttonStyle="white"
          disabled={!(centerLinkIdx < links.length - 1)}
          label={
            <Arrow
              direction="right"
              className={styles.arrow}
              color={centerLinkIdx < links.length - 1 ? "#666666" : "#ededed"}
            />
          }
          onClick={() => setCenterLinkIdx(centerLinkIdx + 1)}
        ></Cta>
      </div>
    </nav>
  );
};
